import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send,
  Bot,
  User
} from 'lucide-react';

const ChatConsultor = ({ data, totalScore, maxPossibleScore, maturityLevel, finalQuestion }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Generar una clave única para este chat basada en los datos del usuario
  const chatStorageKey = `chat_history_${data?.id || 'default'}`;

  // Función para guardar mensajes en localStorage
  const saveMessagesToStorage = useCallback((messagesToSave) => {
    try {
      localStorage.setItem(chatStorageKey, JSON.stringify(messagesToSave));
    } catch (error) {
      console.error('Error al guardar mensajes en localStorage:', error);
    }
  }, [chatStorageKey]);

  // Función para cargar mensajes desde localStorage
  const loadMessagesFromStorage = useCallback(() => {
    try {
      const savedMessages = localStorage.getItem(chatStorageKey);
      if (savedMessages) {
        const parsedMessages = JSON.parse(savedMessages);
        // Convertir las fechas de string a Date objects
        return parsedMessages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error al cargar mensajes desde localStorage:', error);
    }
    return null;
  }, [chatStorageKey]);

  // Función para limpiar el historial del chat
  const clearChatHistory = () => {
    try {
      localStorage.removeItem(chatStorageKey);
      setMessages([]);
    } catch (error) {
      console.error('Error al limpiar historial del chat:', error);
    }
  };

  // Función para hacer scroll automático al final
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Guardar mensajes en localStorage cada vez que cambien
  useEffect(() => {
    if (messages.length > 0) {
      saveMessagesToStorage(messages);
    }
  }, [messages, chatStorageKey, saveMessagesToStorage]);

  // Cargar historial del chat al inicializar
  useEffect(() => {
    const savedMessages = loadMessagesFromStorage();
    
    if (savedMessages && savedMessages.length > 0) {
      // Si hay mensajes guardados, cargarlos
      setMessages(savedMessages);
    } else {
      // Si no hay mensajes guardados, mostrar mensaje de bienvenida
      const welcomeMessage = {
        id: 1,
        type: 'bot',
        content: `¡Hola! Soy Jul.ia, tu consultor experto en IA 🤖✨

He analizado tus resultados y veo que tienes un nivel de madurez **${getMaturityLevelName(maturityLevel)}** con ${totalScore} puntos.

¡Esto es genial! Estás en el camino correcto hacia la transformación digital. 

¿En qué puedo ayudarte hoy? Puedes preguntarme sobre:
• 🎯 Recomendaciones específicas para mejorar
• 📊 Explicaciones detalladas sobre tus resultados  
• 🚀 Cómo implementar soluciones de IA paso a paso
• 📈 Comparaciones con otros niveles de madurez
• 💡 Ideas prácticas para tu PyME

¡Estoy aquí para guiarte en tu viaje hacia la IA!`,
        timestamp: new Date(),
        suggestions: [
          "¿Qué área debería priorizar?",
          "Explícame mis resultados",
          "¿Cómo puedo mejorar mi puntaje?",
          "Dame recomendaciones prácticas"
        ]
      };
      setMessages([welcomeMessage]);
    }
  }, [maturityLevel, totalScore, chatStorageKey, loadMessagesFromStorage]);

  // Función para obtener el nombre del nivel de madurez
  const getMaturityLevelName = (level) => {
    const levels = {
      1: 'Exploración Inicial',
      2: 'Compromiso Emergente', 
      3: 'Integración Operativa',
      4: 'Implementación Avanzada',
      5: 'Liderazgo Transformacional'
    };
    return levels[level] || 'Explorador';
  };

  // Función para calcular puntaje por categoría
  const calculateCategoryScore = (categoryName) => {
    const questionGroups = {
      'Negocio': [
        'La organización tiene una visión formalizada sobre el rol estratégico de la IA en su crecimiento futuro.',
        'El liderazgo ejecutivo está activamente comprometido con el despliegue estratégico de IA.',
        'Existe un equipo o rol formalmente asignado para liderar iniciativas de IA.',
        'Se han priorizado áreas y procesos donde IA puede generar impacto medible.',
        'Se han definido y documentado casos de uso de IA alineados a los objetivos de negocio.',
        'Mi organización destina fondos/recursos a proyectos relacionados con la IA'
      ],
      'Tecnología': [
        'Existe un área o persona encargada de la estrategia de tecnología en la organización.',
        'Los procesos clave de la organización están digitalizados.',
        'Los sistemas actuales están integrados o cuentan con posibilidad de integrarse, y es fácil acceder a la información.',
        'La organización cuenta con un ERP central para la gestión administrativa y CRM para la gestión comercial.',
        'En la organización hay concientización de la ciberseguridad como un aspecto importante a tener en cuenta (protección de activos, evaluación de riesgos, control de accesos, etc).',
        'La empresa cuenta con servicios de nube (AWS, Google Cloud, Azure) para diferentes necesidades de negocio.'
      ],
      'Data': [
        'Los datos se utilizan en la organización para definir objetivos y hacer se rija por los datos (data driven).',
        'La organización utiliza los datos para resolver problemas relacionados con los productos y servicios ofrecidos.',
        'La organización dispone de la infraestructura necesaria para almacenar, gestionar, acceder y recuperar datos de forma eficaz.',
        'La calidad de los datos de mi organización es precisa y coherente.',
        'Los datos de mi organización están bien integrados y están a disposición de los empleados con los derechos de acceso adecuados.'
      ],
      'Equipos': [
        'Hay apertura a nuevas ideas o tecnologías.',
        'Uno o más miembros del equipo han mostrado interés en aprender sobre IA',
        'Se han identificado roles que podrían beneficiarse con herramientas inteligentes.',
        'Se ha incentivado la formación básica (videos, cursos, webinars) en temas de IA.',
        'Se comunican claramente los beneficios de la IA al equipo.',
        'Se aborda el miedo al reemplazo con capacitación y transparencia.'
      ],
      'Procesos': [
        'Las actividades principales de la empresa se rigen por procesos estandarizados.',
        'Las personas involucradas conocen sus responsabilidades dentro de cada proceso.',
        'Los procesos clave están documentados (manuales, diagramas, SOPs).',
        'Existen indicadores de desempeño de procesos (KPIs Operativos).',
        'Se trabaja en identificar y automatizar procesos repetitivos de manera continua.',
        'Los procesos actuales soportarían un crecimiento significativo en volumen o complejidad.'
      ],
      'Aplicaciones iniciales': [
        'Asistentes de contenido:¿Se utilizan herramientas de IA para redactar, corregir o generar contenidos (por ejemplo, para blogs, redes sociales, presentaciones)? (Ej: ChatGPT, Jasper, Copy.ai)',
        'Chatbots y atención automática: ¿Se ha considerado implementar un chatbot en la web o redes sociales?',
        'Clasificación automática de correos o consultas:¿Se están explorando sistemas de IA que prioricen, clasifiquen o respondan correos electrónicos o solicitudes automáticamente?',
        'Análisis de datos de clientes:¿Hay interés en usar IA para analizar datos de ventas o comportamiento de clientes?',
        'Análisis de satisfacción del cliente:¿Se utilizan herramientas de IA para analizar encuestas, comentarios o reseñas de clientes y extraer insights sobre su satisfacción?',
        'Optimización de procesos internos:¿Se consideran herramientas de IA para detectar cuellos de botella o mejorar procesos administrativos o logísticos?',
        'Sistemas de recomendación de productos o servicios:¿Se explora el uso de IA para personalizar recomendaciones a clientes, basadas en su historial de compras o navegación?',
        'Detección de anomalías o fraudes:¿Se están considerando soluciones de IA para detectar transacciones inusuales, fraudes o riesgos operativos?',
        'Soporte en procesos de reclutamiento:¿Se evalúan herramientas de IA para ayudar en la preselección de candidatos o análisis de CVs?',
        'Automatización de tareas financieras básicas:¿Se utilizan sistemas de IA para conciliaciones contables, gestión de gastos o reportes automáticos?'
      ]
    };

    const getPropertyValue = (property) => {
      if (!property) return 'No disponible';
      if (property.type === 'rich_text' && property.rich_text) {
        return property.rich_text[0]?.plain_text || 'No disponible';
      }
      if (property.type === 'title' && property.title) {
        return property.title[0]?.plain_text || 'No disponible';
      }
      if (property.type === 'email' && property.email) {
        return property.email;
      }
      return 'No disponible';
    };

    const isScoreQuestion = (value, groupName) => {
      const numValue = parseInt(value);
      if (groupName === 'Aplicaciones iniciales') {
        return !isNaN(numValue) && numValue >= 1 && numValue <= 3;
      }
      return !isNaN(numValue) && numValue >= 1 && numValue <= 4;
    };

    const questions = questionGroups[categoryName] || [];
    let totalScore = 0;
    let maxPossibleScore = 0;

    questions.forEach(question => {
      const value = data?.properties?.[question];
      if (value) {
        const score = getPropertyValue(value);
        if (isScoreQuestion(score, categoryName)) {
          totalScore += parseInt(score);
          const maxScoreForQuestion = categoryName === 'Aplicaciones iniciales' ? 3 : 4;
          maxPossibleScore += maxScoreForQuestion;
        }
      }
    });

    return {
      total: totalScore,
      max: maxPossibleScore,
      average: questions.length > 0 ? (totalScore / questions.length) : 0
    };
  };

  // Función para obtener recomendaciones por categoría
  const getRecommendations = (categoryName, level) => {
    const recommendations = {
      'Negocio': {
        1: [
          "🎯 Define tu visión de IA: Organiza una sesión de 2 horas con tu equipo directivo para definir cómo la IA puede ayudar a tu negocio.",
          "📊 Identifica 3 casos de uso prioritarios: Haz una lista de 5 procesos repetitivos y elige los 3 más importantes.",
          "💰 Asigna un presupuesto inicial: Destina el 2-3% de tu presupuesto anual a proyectos de IA ($500-1000 USD para empezar)."
        ],
        2: [
          "👥 Designa un 'Champion' de IA: Asigna a una persona para liderar las iniciativas de IA.",
          "📈 Establece métricas de éxito: Define 2-3 KPIs específicos para medir el impacto de la IA.",
          "🤝 Busca aliados estratégicos: Conecta con otras PyMEs que estén implementando IA."
        ],
        3: [
          "🎯 Desarrolla una hoja de ruta de IA: Crea un plan de 6-12 meses con objetivos específicos.",
          "📊 Implementa un sistema de seguimiento: Usa herramientas como Trello para trackear el progreso.",
          "🚀 Lanza tu primer piloto: Implementa una solución de IA simple pero impactante."
        ]
      },
      'Tecnología': {
        1: [
          "☁️ Migra a la nube: Si aún no lo has hecho, migra tus datos a Google Workspace o Microsoft 365.",
          "🔒 Mejora tu seguridad básica: Implementa autenticación de dos factores y actualiza software.",
          "📱 Digitaliza un proceso manual: Elige un proceso en papel y digitalízalo con Google Forms."
        ],
        2: [
          "🔗 Integra tus sistemas: Conecta tu CRM con tu sistema de facturación y email marketing.",
          "📊 Implementa un dashboard básico: Usa Google Data Studio para crear métricas en tiempo real.",
          "🛠️ Actualiza tu hardware: Asegúrate de que todos los dispositivos tengan al menos 8GB de RAM."
        ],
        3: [
          "🤖 Implementa automatización básica: Usa Zapier para automatizar tareas repetitivas.",
          "📈 Adopta analytics avanzado: Implementa Google Analytics 4 y herramientas de análisis.",
          "🔧 Establece un protocolo de respaldo: Configura respaldos automáticos en la nube."
        ]
      },
      'Data': {
        1: [
          "📋 Haz un inventario de datos: Lista todos los datos que recopilas: clientes, ventas, inventario.",
          "🗂️ Organiza tus datos: Crea carpetas estructuradas en Google Drive con nombres consistentes.",
          "📊 Empieza a usar datos para decisiones: Antes de tomar decisiones, revisa los datos relevantes."
        ],
        2: [
          "🔍 Implementa un CRM básico: Usa HubSpot (gratis) para centralizar información de clientes.",
          "📈 Crea reportes mensuales: Establece un proceso para generar reportes con métricas clave.",
          "🎯 Define tus KPIs principales: Identifica 3-5 métricas que realmente importan para tu negocio."
        ],
        3: [
          "🔗 Integra todas tus fuentes de datos: Conecta CRM, facturación, redes sociales y website.",
          "📊 Implementa dashboards en tiempo real: Crea dashboards que se actualicen automáticamente.",
          "🎯 Desarrolla modelos predictivos básicos: Usa Excel para crear proyecciones basadas en tendencias."
        ]
      },
      'Equipos': {
        1: [
          "🎓 Organiza una charla sobre IA: Invita a un experto o muestra un video de 30 minutos sobre IA.",
          "💡 Identifica 'early adopters': Encuentra 2-3 personas en tu equipo más abiertas a la tecnología.",
          "🤝 Comunica los beneficios claramente: Explica cómo la IA los ayudará a ser más eficientes."
        ],
        2: [
          "📚 Crea un plan de capacitación: Diseña un programa de 4-6 semanas usando recursos gratuitos.",
          "🎯 Asigna roles específicos: Define qué persona será responsable de cada herramienta de IA.",
          "🏆 Crea incentivos por adopción: Reconoce públicamente a quienes adopten nuevas herramientas."
        ],
        3: [
          "🎓 Implementa capacitación avanzada: Invierte en cursos especializados para tu equipo.",
          "🔄 Establece un proceso de feedback: Crea reuniones mensuales para compartir experiencias.",
          "🚀 Fomenta la experimentación: Dale al equipo tiempo libre para experimentar con IA."
        ]
      },
      'Procesos': {
        1: [
          "📋 Crea una política de privacidad básica: Documenta cómo recopilas, usas y proteges los datos.",
          "🔒 Implementa medidas de seguridad básicas: Configura contraseñas fuertes y autenticación de dos factores.",
          "📝 Documenta tus procesos de datos: Crea un documento simple que explique qué datos recopilas."
        ],
        2: [
          "⚖️ Revisa el cumplimiento legal: Asegúrate de cumplir con las regulaciones locales de protección de datos.",
          "🛡️ Implementa un plan de respuesta a incidentes: Crea un protocolo para brechas de seguridad.",
          "👥 Capacita al equipo en seguridad: Organiza una sesión sobre buenas prácticas de seguridad."
        ],
        3: [
          "📊 Implementa auditorías regulares: Establece revisiones trimestrales de procesos de datos.",
          "🎯 Desarrolla un marco ético de IA: Crea principios claros sobre cómo usar la IA responsablemente.",
          "🔄 Establece un comité de procesos: Forma un grupo que revise nuevos usos de IA."
        ]
      },
      'Aplicaciones iniciales': {
        1: [
          "🤖 Implementa un chatbot básico: Usa Chatfuel o ManyChat para responder preguntas frecuentes.",
          "✍️ Adopta herramientas de contenido: Empieza usando ChatGPT para generar contenido.",
          "📧 Automatiza emails de seguimiento: Configura secuencias automáticas con Mailchimp."
        ],
        2: [
          "📊 Implementa análisis de clientes: Usa Google Analytics para analizar comportamiento de clientes.",
          "🎯 Personaliza recomendaciones: Implementa un sistema básico de recomendaciones en tu website.",
          "🔍 Automatiza la clasificación de leads: Usa HubSpot para clasificar leads automáticamente."
        ],
        3: [
          "📈 Implementa análisis predictivo: Usa Tableau para crear modelos que predigan tendencias.",
          "🛡️ Implementa detección de fraudes: Usa Stripe Radar para detectar transacciones sospechosas.",
          "🔄 Automatiza procesos internos: Implementa RPA usando UiPath para automatizar tareas repetitivas."
        ]
      }
    };

    return recommendations[categoryName]?.[level] || [];
  };

  // Función para procesar mensajes del usuario con IA real
  const processUserMessage = async (message) => {
    try {
      // Crear contexto basado en los resultados del usuario
      const userContext = `Usuario con nivel de madurez ${getMaturityLevelName(maturityLevel)} (${totalScore}/${maxPossibleScore} puntos). 
      
Resultados por categoría:
${['Negocio', 'Tecnología', 'Data', 'Equipos', 'Procesos', 'Aplicaciones iniciales'].map(category => {
  const score = calculateCategoryScore(category);
  return `• ${category}: ${score.average.toFixed(1)}/4`;
}).join('\n')}

${finalQuestion && Object.entries(finalQuestion).length > 0 ? `
CONTEXTO ESPECÍFICO DEL USUARIO:
El usuario mencionó una tarea/proceso específico que podría mejorarse con IA:
"${Object.entries(finalQuestion)[0][1] ? Object.entries(finalQuestion)[0][1].rich_text?.[0]?.plain_text || 'No especificado' : 'No especificado'}"

Usa esta información para dar recomendaciones más personalizadas y específicas sobre cómo la IA podría ayudar con su caso particular.
` : ''}

El usuario está consultando sobre: ${message}`;

      const API_BASE_URL = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001' 
        : (window.API_BASE_URL || process.env.REACT_APP_API_URL || '');
      
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          context: userContext
        })
      });

      const data = await response.json();
      
      if (data.success) {
        return data.response;
      } else {
        // Si falla la API, usar respuestas predefinidas como fallback
        return getFallbackResponse(message);
      }
    } catch (error) {
      console.error('Error al conectar con IA:', error);
      // Fallback a respuestas predefinidas
      return getFallbackResponse(message);
    }
  };

  // Función de fallback con respuestas predefinidas
  const getFallbackResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    // Detectar intención del usuario
    if (lowerMessage.includes('área') || lowerMessage.includes('priorizar') || lowerMessage.includes('enfoque')) {
      return getAreaRecommendation();
    }
    
    if (lowerMessage.includes('resultado') || lowerMessage.includes('explica') || lowerMessage.includes('significa')) {
      return getResultsExplanation();
    }
    
    if (lowerMessage.includes('mejorar') || lowerMessage.includes('puntaje') || lowerMessage.includes('subir')) {
      return getImprovementTips();
    }
    
    if (lowerMessage.includes('recomendación') || lowerMessage.includes('práctico') || lowerMessage.includes('acción')) {
      return getPracticalRecommendations();
    }
    
    if (lowerMessage.includes('negocio') || lowerMessage.includes('estrategia')) {
      return getCategoryDetails('Negocio');
    }
    
    if (lowerMessage.includes('tecnología') || lowerMessage.includes('infraestructura')) {
      return getCategoryDetails('Tecnología');
    }
    
    if (lowerMessage.includes('datos') || lowerMessage.includes('data')) {
      return getCategoryDetails('Data');
    }
    
    if (lowerMessage.includes('equipo') || lowerMessage.includes('cultura')) {
      return getCategoryDetails('Equipos');
    }
    
    if (lowerMessage.includes('procesos') || lowerMessage.includes('seguridad')) {
      return getCategoryDetails('Procesos');
    }
    
    if (lowerMessage.includes('aplicación') || lowerMessage.includes('implementar')) {
      return getCategoryDetails('Aplicaciones iniciales');
    }
    
    // Respuesta por defecto
    return `Entiendo tu pregunta sobre "${message}". 

Como tu consultor de IA, puedo ayudarte con:
• Análisis de tus resultados específicos
• Recomendaciones personalizadas por área
• Planes de acción prácticos
• Explicaciones sobre madurez en IA

¿Podrías ser más específico sobre qué área te interesa más? Por ejemplo:
- "¿Qué área debería priorizar?"
- "Explícame mis resultados en Negocio"
- "Dame recomendaciones para Tecnología"`;
  };

  // Función para obtener recomendación de área prioritaria
  const getAreaRecommendation = () => {
    const categories = ['Negocio', 'Tecnología', 'Data', 'Equipos', 'Procesos', 'Aplicaciones iniciales'];
    let lowestCategory = null;
    let lowestScore = 5;
    
    categories.forEach(categoryName => {
      const score = calculateCategoryScore(categoryName);
      if (score.average < lowestScore) {
        lowestScore = score.average;
        lowestCategory = categoryName;
      }
    });
    
    const recommendations = getRecommendations(lowestCategory, getCategoryLevel(lowestScore));
    
    return `🎯 **Área Prioritaria: ${lowestCategory}**

Tu puntaje en ${lowestCategory} es de ${lowestScore.toFixed(1)}/4, lo que indica que es el área con mayor potencial de mejora.

**Recomendaciones prioritarias:**

${recommendations.slice(0, 3).map((rec, index) => `${index + 1}. ${rec}`).join('\n\n')}

¿Te gustaría que profundice en alguna de estas recomendaciones o en otra área específica?`;
  };

  // Función para explicar resultados
  const getResultsExplanation = () => {
    const levelName = getMaturityLevelName(maturityLevel);
    const levelDescriptions = {
      'Explorador': 'Estás comenzando tu viaje en IA. Es el momento perfecto para aprender y sentar las bases.',
      'Implementador': 'Tienes bases sólidas y estás listo para implementar soluciones reales de IA.',
      'Visionario': '¡Excelente! Estás liderando la transformación digital en tu organización.',
      'Pionero': 'Eres un referente en IA. Tu organización está a la vanguardia tecnológica.',
      'Líder Transformacional': '¡Increíble! Eres un líder que inspira a otros en el uso de IA.'
    };
    
    return `📊 **Análisis de tus Resultados**

**Nivel Actual:** ${levelName} (${totalScore}/${maxPossibleScore} puntos)
**Descripción:** ${levelDescriptions[levelName]}

**Desglose por Áreas:**
${['Negocio', 'Tecnología', 'Data', 'Equipos', 'Procesos', 'Aplicaciones iniciales'].map(category => {
  const score = calculateCategoryScore(category);
  return `• ${category}: ${score.average.toFixed(1)}/4`;
}).join('\n')}

**Interpretación:**
- Puntajes 1-2: Área de oportunidad (enfoque prioritario)
- Puntajes 2.5-3: Área en desarrollo (consolidar avances)
- Puntajes 3.5-4: Área fuerte (optimizar y escalar)

¿Te gustaría que profundice en alguna área específica?`;
  };

  // Función para obtener tips de mejora
  const getImprovementTips = () => {
    return `🚀 **Plan de Mejora Personalizado**

Basándome en tu nivel ${getMaturityLevelName(maturityLevel)}, aquí tienes un plan de 3 pasos:

**Paso 1: Fortalece las bases (1-2 meses)**
• Identifica tu área más débil y enfócate en ella
• Implementa 2-3 acciones de bajo costo y alto impacto
• Mide el progreso semanalmente

**Paso 2: Acelera el crecimiento (2-4 meses)**
• Integra herramientas de IA en procesos clave
• Capacita a tu equipo en las nuevas tecnologías
• Establece métricas de éxito claras

**Paso 3: Escala y optimiza (4-6 meses)**
• Automatiza procesos repetitivos
• Desarrolla casos de uso avanzados
• Comparte conocimientos con otras organizaciones

**Próximo nivel:** ${maturityLevel < 5 ? getMaturityLevelName(maturityLevel + 1) : 'Ya eres líder!'}
**Puntos necesarios:** ${maturityLevel < 5 ? (maturityLevel === 1 ? 31 - totalScore : maturityLevel === 2 ? 61 - totalScore : maturityLevel === 3 ? 91 - totalScore : 121 - totalScore) : 0}

¿En qué área específica te gustaría empezar?`;
  };

  // Función para obtener recomendaciones prácticas
  const getPracticalRecommendations = () => {
    return `💡 **Recomendaciones Prácticas Inmediatas**

**Esta semana:**
• Haz un inventario de tus procesos más repetitivos
• Identifica 3 herramientas de IA gratuitas que puedas probar
• Programa una reunión con tu equipo para discutir IA

**Este mes:**
• Implementa una herramienta de IA simple (ej: ChatGPT para contenido)
• Digitaliza al menos un proceso manual
• Establece un presupuesto inicial para proyectos de IA

**Próximos 3 meses:**
• Lanza tu primer piloto de IA
• Capacita a tu equipo en herramientas básicas
• Mide y documenta los resultados

**Recursos recomendados:**
• Cursos gratuitos: Google AI, Microsoft Learn
• Herramientas: ChatGPT, Zapier, Google Analytics
• Comunidades: LinkedIn grupos de IA para PyMEs

¿Te gustaría que te ayude a planificar alguna de estas acciones específicamente?`;
  };

  // Función para obtener detalles de una categoría
  const getCategoryDetails = (categoryName) => {
    const score = calculateCategoryScore(categoryName);
    const level = getCategoryLevel(score.average);
    const recommendations = getRecommendations(categoryName, level);
    
    const categoryIcons = {
      'Negocio': '🎯',
      'Tecnología': '🏗️',
      'Data': '📊',
      'Equipos': '👥',
      'Procesos': '🛡️',
      'Aplicaciones iniciales': '⚡'
    };
    
    return `${categoryIcons[categoryName]} **${categoryName} - Nivel ${level}**

**Tu puntaje:** ${score.average.toFixed(1)}/4 (${score.total}/${score.max} puntos)

**Diagnóstico:**
${level === 1 ? `Estás en la fase inicial de ${categoryName.toLowerCase()}. Es el momento perfecto para sentar las bases y comenzar con acciones simples pero impactantes.` : 
  level === 2 ? `¡Buen trabajo! Tienes una base sólida en ${categoryName.toLowerCase()}. Ahora es momento de consolidar y expandir tus capacidades.` :
  `¡Excelente! Estás en un nivel avanzado de ${categoryName.toLowerCase()}. Ahora puedes enfocarte en optimización y en convertirte en un referente.`}

**Recomendaciones específicas:**

${recommendations.map((rec, index) => `${index + 1}. ${rec}`).join('\n\n')}

¿Te gustaría que profundice en alguna de estas recomendaciones o te ayude a implementarlas?`;
  };

  // Función para obtener el nivel de una categoría
  const getCategoryLevel = (average) => {
    if (average >= 3.5) return 3;
    if (average >= 2.5) return 2;
    return 1;
  };

  // Función para enviar mensaje
  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    try {
      // Obtener respuesta de la IA
      const aiResponse = await processUserMessage(currentMessage);
      
      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: aiResponse,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error al obtener respuesta de IA:', error);
      
      // Respuesta de fallback
      const fallbackResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Lo siento, estoy teniendo problemas técnicos. ¿Podrías intentar de nuevo?',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, fallbackResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  // Función para enviar sugerencia
  const sendSuggestion = async (suggestion) => {
    // Crear mensaje del usuario inmediatamente
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: suggestion,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Obtener respuesta de la IA
      const aiResponse = await processUserMessage(suggestion);
      
      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: aiResponse,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error al obtener respuesta de IA:', error);
      
      // Respuesta de fallback
      const fallbackResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Lo siento, estoy teniendo problemas técnicos. ¿Podrías intentar de nuevo?',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, fallbackResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  // Función para manejar Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-consultor">
      {/* Header del chat */}
      

      {/* Área de mensajes */}
      <div className="chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-avatar">
              {message.type === 'bot' ? <Bot className="icon" /> : <User className="icon" />}
            </div>
            <div className="message-content">
              <div className="message-text">
                {message.content.split('\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
              {message.suggestions && (
                <div className="message-suggestions">
                  {message.suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="suggestion-btn"
                      onClick={() => sendSuggestion(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
              <div className="message-time">
                {message.timestamp.toLocaleTimeString('es-ES', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="message bot">
            <div className="message-avatar">
              <Bot className="icon" />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Área de entrada */}
      <div className="chat-input">
        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Pregúntame sobre tu transformación digital..."
            rows="1"
            className="message-input"
          />
          <button 
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isTyping}
            className="send-btn"
          >
            <Send className="icon" />
          </button>
        </div>
        <div className="input-suggestions">
          <span>Preguntas frecuentes:</span>
          <button onClick={() => sendSuggestion("¿Qué área debería priorizar?")}>
            Área prioritaria
          </button>
          <button onClick={() => sendSuggestion("Explícame mis resultados")}>
            Mis resultados
          </button>
          <button onClick={() => sendSuggestion("¿Cómo puedo mejorar mi puntaje?")}>
            Mejorar puntaje
          </button>
          <button onClick={() => sendSuggestion("¿Y ahora como sigo?")}>
            ¿Y ahora como sigo?
          </button>
          <button
            className="clear-history-btn"
            onClick={clearChatHistory}
            title="Limpiar historial del chat"
          >
            🗑️ Limpiar historial
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatConsultor;
